import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { PillBtn } from "#/components/primitives";
import { confirmEmailChange } from "#/lib/auth-client";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import { sessionKeys, sessionQueries } from "#/lib/useSession";

const searchSchema = z.object({
  token: z.string().min(1),
  email: z.string().email(),
});

export const Route = createFileRoute("/verify-email-change")({
  validateSearch: searchSchema,
  head: () => ({ meta: [{ title: "Confirm email change" }] }),
  errorComponent: InvalidLink,
  component: VerifyEmailChangePage,
});

function VerifyEmailChangePage() {
  const { token, email } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The verification link IS the user's confirmation — they already clicked
  // it in their email client — so modelling this as a query keyed by the
  // link itself (rather than a mutation triggered on mount) lets TanStack
  // Query handle the fire-once semantics natively.
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["confirm-email-change", token, email],
    queryFn: async () => {
      try {
        await confirmEmailChange({ token, email });
        return { ok: true } as const;
      } catch (err) {
        if (err instanceof Error && /404|not.*found/i.test(err.message)) {
          return { ok: false, comingSoon: true } as const;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!data) return;
    if (!data.ok) {
      notify.info("Email change coming soon", {
        description: "This feature isn't enabled yet — try again later.",
      });
      return;
    }
    notify.success("Email updated", {
      description: `Your account email is now ${email}.`,
    });
    void (async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKeys.current });
      await queryClient.ensureQueryData(sessionQueries.current());
      navigate({ to: ROUTES.accountProfile });
    })();
  }, [data, email, navigate, queryClient]);

  return (
    <div data-screen-label="Verify Email Change" className="pb-24">
      <section className="mx-auto w-full max-w-md px-6 pt-16 md:pt-24">
        <h1 className="m-0 mb-3 font-bold font-display text-4xl text-lp-green-dk leading-none tracking-tight">
          Confirming your new email
        </h1>
        <p className="mt-2 mb-10 m-0 font-display text-lp-text">
          Updating your account to <strong>{email}</strong>…
        </p>

        {isPending && (
          <div className="font-display text-lp-text-lt">
            Verifying your link…
          </div>
        )}

        {isError && (
          <div className="rounded-3xl border-2 border-destructive p-6">
            <div className="mb-3 font-display text-destructive">
              {error instanceof Error
                ? error.message
                : "We couldn't confirm this link. It may have expired."}
            </div>
            <PillBtn
              size="sm"
              variant="green"
              onClick={() => navigate({ to: ROUTES.accountProfile })}
            >
              Back to profile
            </PillBtn>
          </div>
        )}
      </section>
    </div>
  );
}

function InvalidLink() {
  return (
    <div className="pb-24">
      <section className="mx-auto w-full max-w-md px-6 pt-16 md:pt-24">
        <h1 className="m-0 mb-3 font-bold font-display text-4xl text-lp-green-dk leading-none tracking-tight">
          Confirmation link invalid
        </h1>
        <p className="mt-2 mb-10 m-0 font-display text-lp-text">
          This email-change link is missing or malformed. Request a new one from
          your profile.
        </p>
        <Link
          to={ROUTES.accountProfile}
          className="text-lp-green-dk underline hover:text-lp-green-mid"
        >
          Back to profile
        </Link>
      </section>
    </div>
  );
}
